/** Integer avalanche used for every random decision in world generation. */
export function hash32(seed: number, x = 0, y = 0, z = 0): number {
  let value = seed >>> 0;
  value ^= Math.imul(x | 0, 0x9e3779b1);
  value ^= Math.imul(y | 0, 0x85ebca77);
  value ^= Math.imul(z | 0, 0xc2b2ae3d);
  value ^= value >>> 16;
  value = Math.imul(value, 0x7feb352d);
  value ^= value >>> 15;
  value = Math.imul(value, 0x846ca68b);
  value ^= value >>> 16;
  return value >>> 0;
}

export function hashFloat(seed: number, x = 0, y = 0, z = 0): number {
  return hash32(seed, x, y, z) / 0x1_0000_0000;
}

export function hashSigned(seed: number, x = 0, y = 0, z = 0): number {
  return hashFloat(seed, x, y, z) * 2 - 1;
}

function fade(value: number): number {
  return value * value * (3 - 2 * value);
}

function lerp(a: number, b: number, amount: number): number {
  return a + (b - a) * amount;
}

/** Continuous seeded value noise in [-1, 1]. */
export function valueNoise2(seed: number, x: number, z: number): number {
  const x0 = Math.floor(x);
  const z0 = Math.floor(z);
  const tx = fade(x - x0);
  const tz = fade(z - z0);
  const a = lerp(hashSigned(seed, x0, 0, z0), hashSigned(seed, x0 + 1, 0, z0), tx);
  const b = lerp(hashSigned(seed, x0, 0, z0 + 1), hashSigned(seed, x0 + 1, 0, z0 + 1), tx);
  return lerp(a, b, tz);
}

export function valueNoise3(seed: number, x: number, y: number, z: number): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const z0 = Math.floor(z);
  const tx = fade(x - x0);
  const ty = fade(y - y0);
  const tz = fade(z - z0);
  const layer = (iy: number): number => {
    const a = lerp(hashSigned(seed, x0, iy, z0), hashSigned(seed, x0 + 1, iy, z0), tx);
    const b = lerp(hashSigned(seed, x0, iy, z0 + 1), hashSigned(seed, x0 + 1, iy, z0 + 1), tx);
    return lerp(a, b, tz);
  };
  return lerp(layer(y0), layer(y0 + 1), ty);
}

export function fractalNoise2(seed: number, x: number, z: number): number {
  let value = 0;
  let amplitude = 0.58;
  let frequency = 1 / 180;
  let normalizer = 0;
  for (let octave = 0; octave < 4; octave += 1) {
    value += valueNoise2(seed + octave * 0x51ed, x * frequency, z * frequency) * amplitude;
    normalizer += amplitude;
    amplitude *= 0.48;
    frequency *= 2.07;
  }
  return value / normalizer;
}

export function smoothstep(edge0: number, edge1: number, value: number): number {
  const amount = Math.max(0, Math.min(1, (value - edge0) / (edge1 - edge0)));
  return amount * amount * (3 - 2 * amount);
}
